import { Dialog, DialogOpenChangeDetails } from "@ark-ui/react";
import { useCallback } from "react";
import classnames from "classnames";

import {
  ParameterTimeSeriesConfiguration,
  ParameterTimeSeriesConfigurationSeriesInner as ParameterTimeSeriesSeries,
} from "@/lib/api";
import type { TimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter.types";
import type { UnitFamilyMap } from "@/lib/parameters/parameter-units.utils";
import "@/components/dialog/dialog.css";
import dialogStyles from "@/features/widget-forms/forms/weather-model-map/weather-model-map-dialog.module.css";
import {
  TimeSeriesConfiguratorBody,
  type ConfiguratorPreviewRender,
} from "@/features/widget-forms/forms/weather-model-map/components/time-series-configurator-body";
import type { TimeSeriesConfiguratorDraft } from "@/features/widget-forms/forms/weather-model-map/time-series-configurator.utils";

import styles from "./modal-styles.module.css";

export type { ParameterTimeSeriesSeries };

interface TimeSeriesConfiguratorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialConfig: ParameterTimeSeriesConfiguration | TimeSeriesConfiguratorDraft;
  parameters: TimeSeriesParameterOption[];
  /** Property-kind unit families forwarded to the configurator body. */
  unitFamilies?: UnitFamilyMap;
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[];
  configuratorIndex: number | null;
  parametersLoading?: boolean;
  onDone: (config: ParameterTimeSeriesConfiguration) => void;
  onCancel: () => void;
  /**
   * Live preview rendered on the right-hand side of the dialog (mirrors the main
   * editor's preview). Receives the in-progress draft as a best-effort
   * {@link ParameterTimeSeriesConfiguration} (or `null`), so the preview reflects
   * live edits.
   */
  renderPreview?: (
    liveConfig: ParameterTimeSeriesConfiguration | null,
  ) => ConfiguratorPreviewRender;
}

/**
 * Dialog presentation of the time-series configurator: a medium modal hosting
 * the paneled configurator body (tab rail + sections) with an optional live
 * preview column.
 */
export function TimeSeriesConfiguratorModal({
  open,
  onOpenChange,
  initialConfig,
  parameters,
  unitFamilies,
  timeSeriesConfigurations,
  configuratorIndex,
  parametersLoading = false,
  onDone,
  onCancel,
  renderPreview,
}: TimeSeriesConfiguratorModalProps) {
  const handleOpenChange = useCallback(
    (details: DialogOpenChangeDetails) => {
      onOpenChange(details.open);
    },
    [onOpenChange],
  );

  const handleDone = useCallback(
    (config: ParameterTimeSeriesConfiguration) => {
      onDone(config);
      onOpenChange(false);
    },
    [onDone, onOpenChange],
  );

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Backdrop className={styles.nestedModalBackdrop} />
      <Dialog.Positioner className={styles.nestedModalPositioner}>
        <Dialog.Content
          className={classnames(
            styles.configuratorMedium,
            styles.configuratorDialog,
            dialogStyles.dialog,
          )}
        >
          {open && (
            <TimeSeriesConfiguratorBody
              key={configuratorIndex ?? "new"}
              initialConfig={initialConfig}
              parameters={parameters}
              unitFamilies={unitFamilies}
              timeSeriesConfigurations={timeSeriesConfigurations}
              configuratorIndex={configuratorIndex}
              parametersLoading={parametersLoading}
              editableTitle
              renderPreview={renderPreview}
              className={styles.configuratorBodyFill}
              onDone={handleDone}
              onCancel={onCancel}
            />
          )}
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
